<template>
  <v-card flat color="transparent" class="note-item--list">
    <div class="d-flex align-start">
      <BulkSelectCheckbox
        v-if="isBulkActionsActive"
        class="mx-3 mt-0"
        :checked="isSelected"
        @click="toggleSelect"
      />

      <!-- Colored icon container matching bookmark favicon size -->
      <div
        class="note-icon-container flex-grow-0 rounded d-flex align-center justify-center"
        :style="iconContainerStyle"
      >
        <PhNote class="note-icon" weight="fill" :style="{ color: iconColor }" />
      </div>

      <div class="card-content flex-grow-1 of-hidden px-4">
        <div class="text-neutral400 d-flex align-center mb-2 mt-md-1 text-caption text-md-body-2">
          <NoteSource :note="note" no-icon />
          <span class="mx-1">&middot;</span>
          <NoteDate :value="note.createdAt" />
        </div>

        <div
          class="note-clickable"
          @click="onContentClick"
        >
          <h4 class="note-title text-body-1 mb-1 font-weight-semibold text-neutral900">
            {{ note.title || $t('common.untitled_note') }}
          </h4>

          <p
            v-if="plainTextContent"
            ref="contentRef"
            class="note-content text-body-2 text-neutral600 mb-3"
          >
            {{ plainTextContent }}<template v-if="isContentTruncated">... <a class="read-more-link text-decoration-underline" @click.stop.prevent="onReadMore">{{ $t('cta.read_more') }}</a></template>
          </p>
        </div>

        <NoteTags v-if="note.tags?.length" class="mb-3" :tags="note.tags" @tag-selected="onTagSelected" />

        <NoteActions
          :note="note"
          :user-id="userId"
          :public-mode="publicMode"
          :membership="membership"
          class="text-text"
          @favorite="$emit('favorite')"
          @save="$emit('save')"
          @add-to-collection="$emit('add-to-collection')"
          @add-to-collection-from-collection="$emit('add-to-collection-from-collection')"
          @comments="$emit('comments')"
          @like="$emit('like')"
          @likes="$emit('likes')"
          @pin-edit="$emit('pin-edit')"
        />
      </div>
    </div>
  </v-card>
</template>

<script setup>
import { ref, computed, onMounted, defineProps, defineEmits } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useDisplay } from 'vuetify'
import { PhNote } from '@phosphor-icons/vue'
import NoteSource from './NoteSource.vue'
import NoteActions from './NoteActions.vue'
import NoteDate from './NoteDate.vue'
import NoteTags from './NoteTags.vue'
import { useBulkActionsV2 } from '@/composables/useBulkActionsV2'
import BulkSelectCheckbox from '@/components/BulkSelectCheckbox.vue'
import { useNoteColors } from '@/utils/noteColors'

const props = defineProps({
  note: {
    type: Object,
    required: true,
  },
  userId: {
    type: String,
    default: null,
  },
  membership: {
    type: Object,
    default: null,
  },
  publicMode: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits([
  'toggle-select',
  'tag-selected',
  'favorite',
  'save',
  'add-to-collection',
  'add-to-collection-from-collection',
  'comments',
  'like',
  'likes',
  'pin-edit',
])

const router = useRouter()
const route = useRoute()
const display = useDisplay()
const { isBulkActionsActive, isItemSelected } = useBulkActionsV2()
const { getNoteColor, getNoteIconColor } = useNoteColors()
const contentRef = ref(null)
const isContentTruncated = ref(false)

const isSelected = computed(() => {
  return isItemSelected(props.note.id)
})

const iconContainerStyle = computed(() => {
  return {
    backgroundColor: getNoteColor(props.note.color)
  }
})

const iconColor = computed(() => {
  return getNoteIconColor(props.note.color)
})

const plainTextContent = computed(() => {
  return props.note.contentPreviewPlainText || ''
})

onMounted(() => {
  checkTruncation()
})

function checkTruncation() {
  if (contentRef.value) {
    isContentTruncated.value = contentRef.value.scrollHeight > contentRef.value.clientHeight
  }
}

function onContentClick() {
  if (isBulkActionsActive.value) {
    toggleSelect()
  } else {
    navigateToDetail()
  }
}

function onReadMore() {
  navigateToDetail()
}

function navigateToDetail() {
  if (props.publicMode) {
    const { username, id } = route.params
    router.push(`/u/${username}/collections/${id}/notes/${props.note.id}`)
    return
  }
  if (props.note.isPrivate) {
    router.push(`/notes/${props.note.id}`)
  } else {
    router.push(`/collections/${props.note.ownerId}/notes/${props.note.id}`)
  }
}

function toggleSelect() {
  emit('toggle-select', props.note)
}

function onTagSelected(tag) {
  emit('tag-selected', tag)
}
</script>

<style lang="scss" scoped>
.note-icon-container {
  width: 24px;
  height: 24px;
  min-width: 24px;

  @media (min-width: 960px) {
    width: 32px;
    height: 32px;
    min-width: 32px;
  }

  .note-icon {
    font-size: 14px;

    @media (min-width: 960px) {
      font-size: 18px;
    }
  }
}

.note-clickable {
  cursor: pointer;
}

.note-title {
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  display: -webkit-box;
  overflow: hidden;
}

.note-content {
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  display: -webkit-box;
  overflow: hidden;
}

.read-more-link {
  cursor: pointer;
  font-weight: 500;
  color: inherit;
}
</style>
